import { useState, useCallback } from 'react';
import { UploadResultRequest, UploadResultResponse } from '../types/lab/SampleResultTypes';
import { uploadGroupResult, uploadIndividualSampleResult, uploadIndividualAnalysisResult } from '../api/labiq/SampleResultApi';

interface UseFileUploadOptions {
	maxRetries?: number;
	retryDelay?: number;
	onProgress?: (progress: number) => void;
	onError?: (error: Error) => void;
	onSuccess?: (response: UploadResultResponse) => void;
}

interface UseFileUploadReturn {
	uploading: boolean;
	progress: number;
	error: string | null;
	uploadGroup: (groupId: number, request: UploadResultRequest) => Promise<UploadResultResponse | null>;
	uploadIndividualSample: (instanceId: number, request: UploadResultRequest) => Promise<UploadResultResponse | null>;
	uploadIndividualAnalysis: (instanceId: number, request: UploadResultRequest) => Promise<UploadResultResponse | null>;
	reset: () => void;
}

export const useFileUpload = (options: UseFileUploadOptions = {}): UseFileUploadReturn => {
	const {
		maxRetries = 3,
		retryDelay = 1000,
		onProgress,
		onError,
		onSuccess
	} = options;

	const [uploading, setUploading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState<string | null>(null);

	const handleProgress = useCallback((progressValue: number) => {
		setProgress(progressValue);
		onProgress?.(progressValue);
	}, [onProgress]);

	const handleError = useCallback((err: Error) => {
		setError(err.message);
		onError?.(err);
	}, [onError]);

	const handleSuccess = useCallback((response: UploadResultResponse) => {
		setProgress(100);
		onSuccess?.(response);
	}, [onSuccess]);

	const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

	const executeUpload = async <T>(
		uploadFn: () => Promise<T>,
		retryCount = 0
	): Promise<T | null> => {
		try {
			const result = await uploadFn();
			return result;
		} catch (err) {
			const error = err as Error;

			const isRetryableError = error.message.includes('network') ||
				error.message.includes('timeout') ||
				error.message.includes('502') ||
				error.message.includes('503') ||
				error.message.includes('504');

			if (retryCount < maxRetries && isRetryableError) {
				console.warn(`Upload attempt ${retryCount + 1} failed, retrying in ${retryDelay}ms...`, error);
				await sleep(retryDelay * Math.pow(2, retryCount));
				return executeUpload(uploadFn, retryCount + 1);
			}

			throw error;
		}
	};

	const uploadGroup = useCallback(async (
		groupId: number,
		request: UploadResultRequest
	): Promise<UploadResultResponse | null> => {
		setUploading(true);
		setProgress(0);
		setError(null);

		try {
			const response = await executeUpload(
				() => uploadGroupResult(groupId, request, handleProgress)
			);

			if (response) {
				handleSuccess(response);
				return response;
			}
			return null;
		} catch (err) {
			const error = err as Error;
			handleError(error);
			return null;
		} finally {
			setUploading(false);
		}
	}, [maxRetries, retryDelay, handleProgress, handleError, handleSuccess]);

	const uploadIndividualSample = useCallback(async (
		instanceId: number,
		request: UploadResultRequest
	): Promise<UploadResultResponse | null> => {
		setUploading(true);
		setProgress(0);
		setError(null);

		try {
			const response = await executeUpload(
				() => uploadIndividualSampleResult(instanceId, request, handleProgress)
			);

			if (response) {
				handleSuccess(response);
				return response;
			}
			return null;
		} catch (err) {
			const error = err as Error;
			handleError(error);
			return null;
		} finally {
			setUploading(false);
		}
	}, [maxRetries, retryDelay, handleProgress, handleError, handleSuccess]);

	const uploadIndividualAnalysis = useCallback(async (
		instanceId: number,
		request: UploadResultRequest
	): Promise<UploadResultResponse | null> => {
		setUploading(true);
		setProgress(0);
		setError(null);

		try {
			const response = await executeUpload(
				() => uploadIndividualAnalysisResult(instanceId, request, handleProgress)
			);

			if (response) {
				handleSuccess(response);
				return response;
			}
			return null;
		} catch (err) {
			const error = err as Error;
			handleError(error);
			return null;
		} finally {
			setUploading(false);
		}
	}, [maxRetries, retryDelay, handleProgress, handleError, handleSuccess]);

	const reset = useCallback(() => {
		setUploading(false);
		setProgress(0);
		setError(null);
	}, []);

	return {
		uploading,
		progress,
		error,
		uploadGroup,
		uploadIndividualSample,
		uploadIndividualAnalysis,
		reset
	};
};